import { useMemo, useState, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { ListEntityType } from 'src/services/lists/lists.types';
import { useGetContactByIdQuery } from 'src/services/contacts/contacts.service';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { openSingleMailerDialog, openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import { ContactDetailSection } from 'src/sections/contacts/components/detail-card';
import ContactDocumentsView from 'src/sections/contacts/components/contact-documents';
import { buildDialNumber, useCommunicationActions } from 'src/sections/admin/integrations/communications/use-communication-actions';

import RecordActionBar from '../record-action-bar';
import RecordPanelHeader from '../record-panel-header';
import { RecordAction, RecordHeaderModel } from '../record-panel.types';

// ----------------------------------------------------------------------

enum TabValue {
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

/**
 * Contact body for the record panel's quick view (JT1-1529). Fetches the contact and feeds the shared
 * header (name, company/role subtitle, email + phone) and action bar (Add to list, Send email, Call,
 * Add task — all labelled buttons). The body shows the contact detail card and the documents. The
 * centred overlay (name click) renders the full ContactsDetailView instead.
 */
export default function ContactRecordView({ id }: { id: string }) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();

  const { data: contact, isLoading } = useGetContactByIdQuery(id, { skip: !id });

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.DETAIL);

  const addToList = useBoolean();

  const commsName = contact ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() : undefined;
  const comms = useCommunicationActions(buildDialNumber(contact?.phone), commsName, { entity_type: CommunicationEntityType.CONTACT, entity_id: id });

  const handleSendEmail = useCallback(() => {
    if (!contact?.email) return;
    dispatch(openSingleMailerDialog({
      emails: [contact.email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: id,
      targetType: MailerTargetType.CONTACT,
      targetId: id,
    }));
  }, [contact?.email, dispatch, id]);

  const handleAddTask = useCallback(() => dispatch(openTaskEditorSidesheet({ contactId: id })), [dispatch, id]);

  const actions: RecordAction[] = useMemo(() => [
    { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue },
    { key: 'send-email', label: t('common.send_email'), icon: 'lsicon:email-send-filled', onClick: handleSendEmail, hidden: !contact?.email },
    { key: 'call', label: t('integrations.communications.actions.call'), icon: 'solar:phone-bold', onClick: () => comms.call(), hidden: !comms.commsActive },
    { key: 'add-task', label: t('common.add_task'), icon: 'ic:round-add-task', onClick: handleAddTask },
  ], [t, addToList.onTrue, handleSendEmail, contact?.email, comms, handleAddTask]);

  const header: RecordHeaderModel = useMemo(() => ({
    name: contact ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() : t('common.loading', { defaultValue: 'Loading…' }),
    subtitle: [contact?.position_title, contact?.company_name].filter(Boolean).join(' · ') || undefined,
    meta: [],
    contact: { email: contact?.email, phone: contact?.phone },
    fullProfileHref: paths.dashboard.contacts.detail(id),
  }), [contact, t, id]);

  if (isLoading || !contact) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />
      <RecordActionBar actions={actions} />

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        <Tab value={TabValue.DETAIL} label={t('contacts.detail.tabs.detail', { defaultValue: 'Details' })} />
        <Tab value={TabValue.DOCUMENTS} label={t('contacts.detail.tabs.documents', { defaultValue: 'Documents' })} />
      </Tabs>

      {currentTab === TabValue.DETAIL && (
        <Stack sx={{ px: 3, py: 2 }}>
          <ContactDetailSection contactId={id} />
        </Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <ContactDocumentsView contactId={id} />
        </Stack>
      )}

      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={addToList.onFalse} items={[id]} entityType={ListEntityType.CONTACT} />
      {comms.smsModal}
    </>
  );
}
